import { reactive } from 'vue';
import * as ed from '@noble/ed25519';
import { unzipSync } from 'fflate';
import { parsePackage, type ModuleInfo } from '@vp/module-runtime';
import type { ModuleManifest, Permission, PublisherLink } from '@vp/shared-types';

/**
 * The module catalog in the app (TZ 5.2) and the "official module" check (TZ 4.1):
 * - a module from the catalog comes as an envelope (`package.vpm` + `signature.json`); the app checks
 *   the SHA-256 of the package and the Ed25519 signature with the keys built into this build
 *   (`__VP_SIGNING_KEYS__`), and that the key is not revoked (built in, or reported by the server);
 * - the servers are asked in order (`__VP_SERVERS__`): when one is down the next answers, the last
 *   good one is asked first next time, and the last answer is kept for when none answers (TZ 7);
 * - without any server the app works as before; the catalog tab says it is unavailable.
 */
export interface CatalogModule {
  id: string;
  name: string | Record<string, string>;
  description: string | Record<string, string>;
  version: string;
  latest: string;
  apiVersion: string;
  status: string | null;
  category: string | null;
  author: string;
  license: string | null;
  /** API 2.4 (older servers do not send it). */
  links?: PublisherLink[];
  permissions: Permission[];
  official: boolean;
  size: number;
  logo: string | null;
  screenshots: string[];
  download: string;
}

export interface ModuleSignature {
  format: 1;
  moduleId: string;
  version: string;
  sha256: string;
  signedAt: string;
  keyId: string;
  signature: string;
}

export type VerifyResult = 'ok' | 'hash' | 'key' | 'revoked' | 'signature';

// ---------------------------------------------------------------- the signature

const hex = (b: Uint8Array) => Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
const fromB64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

/** The 32 key bytes of an Ed25519 public key in PEM (SPKI: a 12-byte header, then the key). */
export function pemToRaw(pem: string): Uint8Array {
  const der = fromB64(pem.replace(/-----[^-]+-----/g, '').replace(/\s+/g, ''));
  return der.slice(der.length - 32);
}

export function signedMessage(s: Omit<ModuleSignature, 'signature' | 'format'>): string {
  return `vp-module-signature:1\n${s.moduleId}\n${s.version}\n${s.sha256}\n${s.signedAt}\n${s.keyId}`;
}

/** The package and its signature from a downloaded file, or null when it is a plain package. */
export function openEnvelope(file: Uint8Array): { pkg: Uint8Array; sig: ModuleSignature } | null {
  try {
    const e = unzipSync(file, { filter: (f) => f.name === 'package.vpm' || f.name === 'signature.json' });
    if (!e['package.vpm'] || !e['signature.json']) return null;
    return { pkg: e['package.vpm'], sig: JSON.parse(new TextDecoder().decode(e['signature.json'])) as ModuleSignature };
  } catch {
    return null;
  }
}

export async function verifySignature(pkg: Uint8Array, sig: ModuleSignature, trusted: { id: string; key: string }[], revoked: ReadonlySet<string>): Promise<VerifyResult> {
  const digest = hex(new Uint8Array(await crypto.subtle.digest('SHA-256', pkg as BufferSource)));
  if (digest !== sig.sha256) return 'hash';
  const key = trusted.find((k) => k.id === sig.keyId);
  if (!key) return 'key';
  if (revoked.has(sig.keyId)) return 'revoked';
  try {
    return (await ed.verifyAsync(fromB64(sig.signature), new TextEncoder().encode(signedMessage(sig)), pemToRaw(key.key))) ? 'ok' : 'signature';
  } catch {
    return 'signature';
  }
}

// ---------------------------------------------------------------- servers

const LAST = 'vp-catalog-server';
const CACHE = 'vp-catalog-cache';
const TIMEOUT = 6000;

function servers(): string[] {
  const all = typeof __VP_SERVERS__ !== 'undefined' ? __VP_SERVERS__ : [''];
  let last: string | null = null;
  try {
    last = localStorage.getItem(LAST);
  } catch {
    /* private mode */
  }
  return last !== null && all.includes(last) ? [last, ...all.filter((s) => s !== last)] : all;
}

/** Ask the servers in order; the first that answers (not a server error) wins and is remembered. */
export async function request(path: string): Promise<{ res: Response; base: string }> {
  let lastError: unknown = new Error('no server');
  for (const base of servers()) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), TIMEOUT);
    try {
      // no-cache: the answer may come from a cache only after the server confirms it is current
      const res = await fetch(base + path, { signal: ctrl.signal, credentials: 'omit', referrerPolicy: 'no-referrer', cache: 'no-cache' });
      // a page instead of JSON means there is no server behind this address (local static build)
      const file = path.split('?')[0].endsWith('/download');
      if (res.status >= 500 || (res.ok && !file && !(res.headers.get('content-type') ?? '').includes('json'))) {
        lastError = new Error(`HTTP ${res.status}`);
        continue;
      }
      try {
        localStorage.setItem(LAST, base);
      } catch {
        /* private mode */
      }
      return { res, base };
    } catch (e) {
      lastError = e;
    } finally {
      clearTimeout(timer);
    }
  }
  throw lastError;
}

// ---------------------------------------------------------------- state

export const catalogState = reactive({
  status: 'idle' as 'idle' | 'loading' | 'ok' | 'stale' | 'offline',
  modules: [] as CatalogModule[],
  /** Revoked key ids: built into this build and reported by the server. */
  revoked: new Set<string>(typeof __VP_REVOKED_KEYS__ !== 'undefined' ? __VP_REVOKED_KEYS__ : []),
});

export function trustedKeys(): { id: string; key: string }[] {
  return typeof __VP_SIGNING_KEYS__ !== 'undefined' ? __VP_SIGNING_KEYS__ : [];
}

const absolute = (base: string, url: string | null) => (url ? base + url : null);

export async function loadCatalog(): Promise<void> {
  catalogState.status = 'loading';
  try {
    const { res, base } = await request('/api/v1/catalog');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as { modules: CatalogModule[] };
    catalogState.modules = data.modules.map((m) => ({ ...m, logo: absolute(base, m.logo), screenshots: m.screenshots.map((s) => base + s), download: base + m.download }));
    catalogState.status = 'ok';
    try {
      localStorage.setItem(CACHE, JSON.stringify(catalogState.modules));
    } catch {
      /* private mode or full */
    }
    // revocations the server knows about (the built-in list may be older)
    request('/api/v1/keys')
      .then(async ({ res: r }) => {
        if (!r.ok) return;
        for (const k of ((await r.json()) as { keys: { id: string; revoked: boolean }[] }).keys) if (k.revoked) catalogState.revoked.add(k.id);
      })
      .catch(() => undefined);
  } catch {
    // no server answers: the last answer, if any, marked as possibly out of date
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE) ?? 'null') as CatalogModule[] | null;
      catalogState.modules = cached ?? [];
      catalogState.status = cached?.length ? 'stale' : 'offline';
    } catch {
      catalogState.status = 'offline';
    }
  }
}

/** Installed module that came from the catalog with a signature of a key still trusted. */
export function isOfficial(m: Pick<ModuleInfo, 'official'>): boolean {
  return !!m.official && trustedKeys().some((k) => k.id === m.official!.keyId) && !catalogState.revoked.has(m.official.keyId);
}

/** A newer version in the catalog for an installed official module. */
export function updateFor(m: ModuleInfo): CatalogModule | null {
  const c = catalogState.modules.find((x) => x.id === m.id);
  return c && m.official && newer(c.version, m.version) ? c : null;
}

export function newer(a: string, b: string): boolean {
  const pa = a.split(/[-+]/)[0].split('.').map(Number);
  const pb = b.split(/[-+]/)[0].split('.').map(Number);
  for (let i = 0; i < 3; i++) if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return (pa[i] ?? 0) > (pb[i] ?? 0);
  return false;
}

// ---------------------------------------------------------------- installing

export class CatalogInstallError extends Error {
  constructor(readonly code: 'offline' | 'not-found' | 'damaged' | 'untrusted' | 'revoked' | 'mismatch' | 'invalid', message: string = code) {
    super(message);
  }
}

/** What the confirmation dialog shows: checked package, its manifest and signature. */
export interface PendingInstall {
  data: Uint8Array;
  manifest: ModuleManifest;
  official: { keyId: string; signedAt: string };
  granted: Set<Permission>;
  source: 'catalog' | 'file';
}

export const installState = reactive({ pending: null as PendingInstall | null, busy: false, error: '' });

/** Check an envelope; a bad one throws with a reason the user can understand. */
export async function checkEnvelope(file: Uint8Array, expect?: { id: string; version?: string }): Promise<Omit<PendingInstall, 'granted' | 'source'>> {
  const env = openEnvelope(file);
  if (!env) throw new CatalogInstallError('damaged');
  const result = await verifySignature(env.pkg, env.sig, trustedKeys(), catalogState.revoked);
  if (result === 'hash' || result === 'signature') throw new CatalogInstallError('damaged');
  if (result === 'key') throw new CatalogInstallError('untrusted');
  if (result === 'revoked') throw new CatalogInstallError('revoked');
  let manifest: ModuleManifest;
  try {
    manifest = parsePackage(env.pkg).manifest;
  } catch (e) {
    throw new CatalogInstallError('invalid', e instanceof Error ? e.message : String(e));
  }
  // the signature names the module and the version: the package inside must be that one
  if (manifest.id !== env.sig.moduleId || manifest.version !== env.sig.version || (expect && (expect.id !== manifest.id || (expect.version && expect.version !== manifest.version)))) throw new CatalogInstallError('mismatch');
  return { data: env.pkg, manifest, official: { keyId: env.sig.keyId, signedAt: env.sig.signedAt } };
}

/** Download a module from the catalog and check it (the install link and the Install button). */
export async function fetchForInstall(id: string, version?: string): Promise<Omit<PendingInstall, 'granted' | 'source'>> {
  let v = version;
  if (!v) {
    let res: Response;
    try {
      res = (await request(`/api/v1/catalog/${encodeURIComponent(id)}`)).res;
    } catch {
      throw new CatalogInstallError('offline');
    }
    if (!res.ok) throw new CatalogInstallError('not-found');
    v = ((await res.json()) as { module: { version: string } }).module.version;
  }
  let res: Response;
  try {
    res = (await request(`/api/v1/catalog/${encodeURIComponent(id)}/${encodeURIComponent(v)}/download?for=install`)).res;
  } catch {
    throw new CatalogInstallError('offline');
  }
  if (!res.ok) throw new CatalogInstallError('not-found');
  return checkEnvelope(new Uint8Array(await res.arrayBuffer()), { id, version: v });
}

/** `#install=<id>@<version>` from the showcase's "Install module" button. */
export function installLinkFromHash(hash = location.hash): { id: string; version?: string } | null {
  const m = /^#install=([a-z0-9]+(?:\.[a-z0-9-]+)+)(?:@([\w.+-]+))?$/.exec(decodeURIComponent(hash));
  return m ? { id: m[1], version: m[2] } : null;
}

// ---------------------------------------------------------------- notices (TZ 6)

export interface Notice {
  id: string;
  channel: string;
  level: 'info' | 'warning' | 'important';
  text: { ru?: string; en?: string };
  link: string | null;
  endsAt: string | null;
}

const DISMISSED = 'vp-dismissed-notices';
export const noticeState = reactive({ notices: [] as Notice[], dismissed: readDismissed() });

function readDismissed(): string[] {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED) ?? '[]') as string[];
  } catch {
    return [];
  }
}

/**
 * Ask for the banner: when the app opens (`open` — counted as one anonymous "app opened") and then
 * every hour. No server, no banner: nothing is shown and nothing is reported to the user.
 */
export async function loadNotices(open: boolean): Promise<void> {
  try {
    const { res } = await request(`/api/v1/notices${open ? '?open=1' : ''}`);
    if (!res.ok) return;
    noticeState.notices = ((await res.json()) as { notices: Notice[] }).notices.filter((n) => n.channel === 'banner');
  } catch {
    /* offline: keep what was shown */
  }
}

/** The banner to show now: not closed by the user, not past its end. */
export function currentNotice(now = Date.now()): Notice | null {
  return noticeState.notices.find((n) => !noticeState.dismissed.includes(n.id) && (!n.endsAt || Date.parse(n.endsAt) > now)) ?? null;
}

export function dismissNotice(id: string): void {
  noticeState.dismissed = [...noticeState.dismissed.filter((x) => x !== id), id].slice(-30);
  try {
    localStorage.setItem(DISMISSED, JSON.stringify(noticeState.dismissed));
  } catch {
    /* private mode: closed for this session */
  }
}
